import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled from "styled-components"
import { Button } from "../../../ui/button"
import { Input } from "../../../ui/input"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../ui/select"
import { useForm, useFieldArray, useWatch } from "react-hook-form"
import { z } from 'zod'
import { zodResolver } from "@hookform/resolvers/zod"
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../../../ui/form"
import { Row, Col } from '../../../ui/RowCol'
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group"
import { CalendarIcon } from "lucide-react"
import { Calendar } from "../../../ui/calendar"
import dayjs from 'dayjs'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../../ui/popover"
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../ui/table"
import { Checkbox } from "../../../ui/checkbox"
import { usePatronCategory } from '../../controllers/PatronCategoryController';
import { useMessageType } from '../../controllers/MessageTypeController'
import { useLibrary } from '../../controllers/LibraryController'
import { TypographyH1, TypographyH2, TypographyH3 } from '../../../ui/typography'
import { Card, CardHeader, CardContent } from '../../../ui/card'

const PatronForm = forwardRef(({
    onFormFinish = () => { },
    defaultQuickMode = true,
}, ref) => {
    const thisJS = "PatronForm"
    const formRef = useRef()
    const [quickMode, setQuickMode] = useState(defaultQuickMode)
    const [showPatronRestrictions, setShowPatronRestrictions] = useState(false)
    const dateSchema = z.union([
        z.date(),
        z.string().refine((val) => !isNaN(Date.parse(val)), {
            message: "Invalid date string",
        }).transform((val) => new Date(val)),
        z.undefined()
    ]);
    const debarredObj = z.object({
        "debarred_id": z.number(),
        "type": z.string(),
        "comment": z.string(),
        "expiration": dateSchema.nullish(),
        "created": dateSchema.nullish(),
        "manager_id": z.number().nullish(),
        "wantDelete": z.number().nullish(),
    });
    const messageObj = z.object({
        "borrower_message_preference_id": z.number(),
        "message_attribute_id": z.number(), //設定拉出的
        "message_name": z.string(), //設定拉出的
        "takes_days": z.number().nullish(), // 是否能設定 Days in advance
        "days_in_advance": z.number().nullish(), //填寫值
        "email": z.optional(z.number()),//填寫值
        "wants_digest": z.number().nullish(),//填寫值
        "canset_digest": z.optional(z.number().nullish()),//是否能設定 wants_digest
    });
    const formSchema = z.object({
        "user_id": z.string().nullish().optional(),
        "borrowernumber": z.string().optional(),
        "cardnumber": z.string().optional(),
        "surname": z.string({ required_error: "此為必填", }),
        "firstname": z.string().optional(),
        "middle_name": z.string().optional(),
        "title": z.string().optional(),
        "othernames": z.string().optional(),    //other_name
        "initials": z.string().optional(),
        "pronouns": z.string().optional(),
        "streetnumber": z.string().optional(),  //street_number
        //"streettype": z.string().optional(),    //street_type:資料庫有欄位，但是沒有在表單上
        "address": z.string().optional(),
        "address2": z.string().optional(),
        "city": z.string().optional(),
        "state": z.string().optional(),
        "zipcode": z.string().optional(),       //postal_code
        "country": z.string().optional(),
        "email": z.string().optional(),
        "phone": z.string().optional(),
        "mobile": z.string().optional(),
        "fax": z.string().optional(),
        "emailpro": z.string().optional(),      //secondary_email
        "phonepro": z.string().optional(),      //secondary_phone
        "B_streetnumber": z.string().optional(),    //altaddress_street_number
        //"B_streettype": z.string().optional(),      //altaddress_street_type:資料庫有欄位，但是沒有在表單上
        "B_address": z.string().optional(),         //altaddress_street
        "B_address2": z.string().optional(),        //altaddress_street2
        "B_city": z.string().optional(),            //altaddress_city
        "B_state": z.string().optional(),           //altaddress_state
        "B_zipcode": z.string().optional(),         //altaddress_postal_code
        "B_country": z.string().optional(),         //altaddress_country
        "B_email": z.string().optional(),           //altaddress_email
        "B_phone": z.string().optional(),           //altaddress_phone
        "dateofbirth": dateSchema,                  //date_of_birth
        "branchcode": z.string({ required_error: "此為必填", }),    //library_id
        "categorycode": z.string({ required_error: "此為必填", }),  //category_id
        "dateenrolled": dateSchema,                  //date_enrolled
        "dateexpiry": dateSchema,                      //expiry_date
        "date_renewed": dateSchema.nullish(), 
        "borrowernotes": z.string().optional(),         //staff_notes
        "sex": z.string().optional(),                   //gender
        "password": z.string().optional(),
        "userid": z.string().optional(),                
        "opacnote": z.string().optional(),              //opac_notes
        "contactnote": z.string().optional(),           //altaddress_notes
        "sort1": z.string().optional(),                 //statistics_1
        "sort2": z.string().optional(),                 //statistics_2
        "autorenew_checkouts": z.number().optional(),
        "altcontactfirstname": z.string().optional(),   //altcontact_firstname
        "altcontactsurname": z.string().optional(),     //altcontact_surname
        "altcontactaddress1": z.string().optional(),    //altcontact_address
        "altcontactaddress2": z.string().optional(),    //altcontact_address2
        "altcontactaddress3": z.string().optional(),    //altcontact_city
        "altcontactstate": z.string().optional(),       //altcontact_state
        "altcontactzipcode": z.string().optional(),     //altcontact_postal_code
        "altcontactcountry": z.string().optional(),     //altcontact_country
        "altcontactphone": z.string().optional(),       //altcontact_phone
  
        "debarredcomment": z.string().nullish().optional(),       //?
        "debarredexpriation": dateSchema.nullish(),               //?

        "primary_contact_method": z.string().optional(),
        "protected": z.number().optional(),
        debarred: z.array(debarredObj),
        message: z.array(messageObj)
    })

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {

        },
    })
    const categoryCode = useWatch({
        control: form.control,
        name: "categorycode",
    })
    const MessageName = ({ control, fieldId }) => {
        const messages = useWatch({
            control,
            name: "message",
        })
        let message = messages[fieldId]
        let messageName = message?.message_name
        return (<SubTitle>
            {messageName}
        </SubTitle>)
    }
    const DaysInAdvance = ({ control, field, index, register }) => {
        const messages = useWatch({
            control,
            name: "message",
        })
        let message = messages[index]
        if (message?.takes_days === 1) {
            return (<Input
                type='number'
                key={field.id}
                placeholder="請輸入..."
                {...field}
                {...register}
            />)
        }
        else {
            return (<>-</>)
        }
    }
    const NormalCheckBox = ({ control, field, index, register }) => {
        //const messages = useWatch({ control, name: "message", })
        return (<Checkbox
            checked={field.value === 1}
            onCheckedChange={(checked) => { return checked ? field.onChange(1) : field.onChange(0) }}
        />)
    }
    const DigestsOnlyCheck = ({ control, field, index, register }) => {
        const messages = useWatch({
            control,
            name: "message",
        })
        let message = messages[index]
        if (message?.canset_digest === 1) {
            //const field = form.register(`message[${index}].wants_digest`)
            const iValue = message?.email === 1 && field.value === 1 ? 1 : 0
            if (iValue !== field.value) { field.onChange(iValue) }
            return (<Checkbox
                checked={field.value === 1}
                disabled={message?.email === 0}
                onCheckedChange={(checked) => {
                    return checked
                        ? field.onChange(1)
                        : field.onChange(0)
                }}
            />)
        }
        else {
            return (<>-</>)
        }
    }
    const {
        fields: debarredFields,
    } = useFieldArray({
        control: form.control,
        name: "debarred"
    });
    const {
        fields: messageFields,
        append: messageAppend,
        remove: messageRemove
    } = useFieldArray({
        control: form.control,
        name: "message"
    });
    const onSubmit = (values) => {
        console.log(thisJS, "onSubmit", values)
        onFormFinish(values)
    }
    const onError = (e) => {
        console.log(thisJS, "onError", e)
    }
    //parent component can use function 
    useImperativeHandle(ref, () => {
        return {
            onFormSubmit: () => {
                formRef.current.requestSubmit();
            },
            setValue: (name, value, config) => {
                form.setValue(name, value, config)
            },
            setValues: (values) => {
                if (typeof values === 'object') {
                    console.log(thisJS, "setValues", values)
                    Object.keys(values).forEach((element) => {
                        form.setValue(element, values[element], { shouldValidate: true, shouldDirty: true, shouldTouch: true })
                    })
                }
            },
            getValues: (name) => {
                return form.getValues(name)
            },
        };
    }, []);




    const {
        PatronCategoryData,
        getPatronCategoryData,
        PatronCategoryTypeData,
        getPatronCategoryTypeData
    } = usePatronCategory({
        onBefore: () => { },
        onFinish: ({ axiosMode, status, values }) => {
            if (axiosMode === "get") {
                if (status === "success") {

                }
            }
        }
    })
    const {
        MessageTypeData,
        getMessageTypeData
    } = useMessageType({
        onBefore: () => { },
        onFinish: ({ axiosMode, status, values }) => { }
    })
    const {
        LibraryData,
        getLibraryData
    } = useLibrary({
        onBefore: () => { },
        onFinish: ({ axiosMode, status, values }) => { }
    })

    useEffect(() => {
        getPatronCategoryTypeData({})
        getMessageTypeData({})
        getLibraryData({})
        getPatronCategoryData({})
    }, [])
    useEffect(() => {
        if (form.getValues('message').length === 0) {
            form.setValue('message', MessageTypeData) 
        }
    }, [MessageTypeData])
    return (
        <Row gutter={[8, 8]} >
            <Col>
                <Button onClick={() => {setQuickMode(!quickMode) }}>
                    {quickMode ? "Show More" : "Quick add"}
                </Button>
            </Col>
            <Col span={24}>
                <Form {...form}>
                    <form ref={formRef} autoComplete="off" onSubmit={form.handleSubmit(onSubmit, onError)}>
                        <Row hidden={!quickMode}>
                            <Col span={24}>
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            Quick add ({categoryCode})
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row gutter={[16, 16]}>
                                            <Col span={24}>
                                                <Row>
                                                    <Col>
                                                        <FormField
                                                            control={form.control}
                                                            name="surname"
                                                            render={({ field }) => (
                                                                <Row gutter={[8, 8]} wrap={false} align="middle">
                                                                    <Col>
                                                                        <SubTitle>Surname :</SubTitle>
                                                                    </Col>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <Em10>
                                                                                    <Input
                                                                                        key={field.id} placeholder="請輸入..."
                                                                                        {...field}
                                                                                    />
                                                                                </Em10>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    </Col>
                                                                </Row>

                                                            )}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col span={24}>
                                                <Row>
                                                    <Col>
                                                        <FormField
                                                            control={form.control}
                                                            name="cardnumber"
                                                            render={({ field }) => (
                                                                <Row gutter={[8, 8]} wrap={false} align="middle">
                                                                    <Col>
                                                                        <SubTitle>Card number :</SubTitle>
                                                                    </Col>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <Em10>
                                                                                    <Input
                                                                                        key={field.id} placeholder="請輸入..."
                                                                                        {...field}
                                                                                    />
                                                                                </Em10>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                            <FormDescription >

                                                                            </FormDescription>
                                                                        </FormItem>
                                                                    </Col>
                                                                </Row>

                                                            )}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="branchcode"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Library:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Select
                                                                            key={field.id}
                                                                            onValueChange={field.onChange}
                                                                            defaultValue={field.value} value={field.value}>
                                                                            <FormControl>
                                                                                <SelectTrigger>
                                                                                    <SelectValue />
                                                                                </SelectTrigger>
                                                                            </FormControl>
                                                                            <SelectContent>
                                                                                {LibraryData.map((value, index) => {
                                                                                    return <SelectItem key={field.id + "_" + index} value={value?.branchcode}>{value?.branchname}</SelectItem>
                                                                                })}
                                                                            </SelectContent>
                                                                        </Select>

                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="categorycode"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Category:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Select
                                                                            onValueChange={field.onChange}
                                                                            value={field.value}
                                                                        >
                                                                            <FormControl>
                                                                                <SelectTrigger
                                                                                >
                                                                                    <SelectValue />
                                                                                </SelectTrigger>
                                                                            </FormControl>
                                                                            <SelectContent>
                                                                                {
                                                                                    PatronCategoryData.map((value, index) => {
                                                                                        return <SelectItem key={field.id + "_" + index} value={value?.categorycode}>{value?.description}</SelectItem>
                                                                                    })
                                                                                }
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="userid"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Username:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>
                        </Row>
                        <Row hidden={quickMode} gutter={[16, 16]}>
                            {/* Patron Identity Section */}
                            <Col xxl={8} xl={8} lg={8} md={12} sm={12} xs={24} >
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            Patron Identity
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row gutter={[8, 8]}>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="borrowernumber"
                                                    render={({ field }) => (
                                                        <Row hidden={true} gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>borrowernumber:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="title"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Salutation:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Select
                                                                            onValueChange={field.onChange}
                                                                            defaultValue={field.value} value={field.value}>
                                                                            <FormControl>
                                                                                <SelectTrigger
                                                                                >
                                                                                    <SelectValue />
                                                                                </SelectTrigger>
                                                                            </FormControl>
                                                                            <SelectContent>
                                                                                <SelectItem value="Mr">Mr</SelectItem>
                                                                                <SelectItem value="Mrs">Mrs</SelectItem>
                                                                                <SelectItem value="Miss">Miss</SelectItem>
                                                                                <SelectItem value="Ms">Ms</SelectItem>
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="surname"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Surname (Required) :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="firstname"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>First Name (Required) :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="middle_name"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Middle Name :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="dateofbirth"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Date of Birth :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormItem>
                                                                        <Popover>
                                                                            <PopoverTrigger asChild>
                                                                                <FormControl>
                                                                                    <Button
                                                                                        variant={"outline"}
                                                                                    >
                                                                                        {field.value ? (
                                                                                            dayjs(field.value).format("MM/DD/YYYY")
                                                                                        ) : (
                                                                                            <span>選擇日期</span>
                                                                                        )}
                                                                                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                                                    </Button>
                                                                                </FormControl>
                                                                            </PopoverTrigger>
                                                                            <PopoverContent className="w-auto p-0" align="start">
                                                                                <Calendar
                                                                                    mode="single"
                                                                                    selected={field.value}
                                                                                    onSelect={field.onChange}
                                                                                    disabled={(date) =>
                                                                                        date > new Date("2020-12-31") || date < new Date("1921-01-01")
                                                                                    }
                                                                                    initialFocus
                                                                                    {...form.register("enrolmentperioddate", {
                                                                                        required: false
                                                                                    })}
                                                                                />
                                                                            </PopoverContent>
                                                                        </Popover>
                                                                    </FormItem>
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="initials"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Initials :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="pronouns"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Pronouns :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="othernames"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Other Name :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="sex"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Gender :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Select
                                                                            onValueChange={field.onChange}
                                                                            defaultValue={field.value} value={field.value}>
                                                                            <FormControl>
                                                                                <SelectTrigger
                                                                                >
                                                                                    <SelectValue />
                                                                                </SelectTrigger>
                                                                            </FormControl>
                                                                            <SelectContent>
                                                                                <SelectItem value="M">Male</SelectItem>
                                                                                <SelectItem value="F">Female</SelectItem>
                                                                                <SelectItem value="O">Other</SelectItem>
                                                                                <SelectItem value="N">None specified</SelectItem>
                                                                            </SelectContent>
                                                                        </Select>

                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>

                            {/* Main Address Section */}
                            <Col xxl={8} xl={8} lg={8} md={12} sm={12} xs={24}>
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            Main Address
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row gutter={[8, 8]}>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="streetnumber"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Street Number :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="address"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Address  :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="address2"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Address 2 :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="city"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>City :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="state"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>State :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="zipcode"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>ZIP/Postal Code :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="country"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Country :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>

                            {/*  Information Section */}
                            <Col xxl={8} xl={8} lg={8} md={12} sm={12} xs={24}>
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            Information
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row gutter={[8, 8]}>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="phone"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Primary Phone:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <Row>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <Em10>
                                                                                    <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                                </Em10>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    </Col>
                                                                    <Col>
                                                                        <FormDescription>
                                                                            Shows on transit slips
                                                                        </FormDescription>
                                                                    </Col>
                                                                </Row>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="phonepro"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Secondary Phone :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="mobile"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Other Phone :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="email"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Primary Email :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="emailpro"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Secondary Email :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="fax"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Fax :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="primary_contact_method"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">

                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Select
                                                                            onValueChange={field.onChange}
                                                                            defaultValue={field.value} value={field.value}>
                                                                            <FormLabel >
                                                                                <SubTitle>Main  Method :</SubTitle>
                                                                            </FormLabel>
                                                                            <FormControl>
                                                                                <SelectTrigger
                                                                                >
                                                                                    <SelectValue />
                                                                                </SelectTrigger>
                                                                            </FormControl>
                                                                            <SelectContent>
                                                                                <SelectItem value="phone">Primary Phone</SelectItem>
                                                                                <SelectItem value="phonepro">Secondary Phone</SelectItem>
                                                                                <SelectItem value="mobile">Other Phone </SelectItem>
                                                                                <SelectItem value="email">Primary Email</SelectItem>
                                                                                <SelectItem value="emailpro">Secondary Email </SelectItem>
                                                                                <SelectItem value="fax">Fax</SelectItem>
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>

                            {/* Address Section */}
                            <Col xxl={8} xl={8} lg={8} md={12} sm={12} xs={24}>
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            Alternate Address
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row gutter={[16, 16]}>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="B_streetnumber"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Street number:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="B_address"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Address:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="B_address2"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Address 2 :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="B_city"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>City :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="B_state"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>State :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="B_zipcode"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>ZIP/Postal Code :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="B_country"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Country :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="B_phone"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Phone :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="B_email"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Email :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="contactnote"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> Note :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>

                            {/*Contact Section */}
                            <Col xxl={8} xl={8} lg={8} md={12} sm={12} xs={24}>
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            Alternate Contact
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row gutter={[8, 8]}>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="altcontactsurname"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> Surname :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="altcontactfirstname"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> First Name :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="altcontactaddress1"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> Address:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="altcontactaddress2"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> Address 2 :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="altcontactaddress3"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> City :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="altcontactstate"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> State :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="altcontactzipcode"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> ZIP/Postal Code :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="altcontactcountry"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> Country :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="altcontactphone"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle> Phone :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>

                            {/* Library Management Section */}
                            <Col xxl={8} xl={8} lg={8} md={12} sm={12} xs={24}>
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            Library Management
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row gutter={[8, 8]}>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="cardnumber"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Card Number :</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <Row>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <Em10>
                                                                                    <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                                </Em10>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    </Col>
                                                                    <Col>
                                                                        <FormDescription>
                                                                            Card number must be between 1 and 32 characters.
                                                                        </FormDescription>
                                                                    </Col>
                                                                </Row>
                                                            </Col>

                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="branchcode"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Library:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Select
                                                                            onValueChange={field.onChange}
                                                                            defaultValue={field.value} value={field.value}>
                                                                            <FormControl>
                                                                                <SelectTrigger>
                                                                                    <SelectValue />
                                                                                </SelectTrigger>
                                                                            </FormControl>
                                                                            <SelectContent>
                                                                                {LibraryData.map((value, index) => {
                                                                                    return <SelectItem key={field.value + "_" + index } value={value?.branchcode}>{value?.branchname}</SelectItem>
                                                                                })}
                                                                            </SelectContent>
                                                                        </Select>

                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="categorycode"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Category:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Select
                                                                            onValueChange={field.onChange}
                                                                            defaultValue={field.value} value={field.value}>
                                                                            <FormControl>
                                                                                <SelectTrigger>
                                                                                    <SelectValue />
                                                                                </SelectTrigger>
                                                                            </FormControl>
                                                                            <SelectContent>
                                                                                {
                                                                                    PatronCategoryData.map((value, index) => {
                                                                                        return <SelectItem key={field.value + "_" + index} value={value?.categorycode}>{value?.description}</SelectItem>
                                                                                    })
                                                                                }

                                                                            </SelectContent>
                                                                        </Select>

                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="sort1"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Sort 1:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <Row>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <Em10>
                                                                                    <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                                </Em10>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    </Col>
                                                                </Row>
                                                            </Col>

                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="sort2"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Sort 2:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <Row>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <Em10>
                                                                                    <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                                </Em10>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    </Col>
                                                                </Row>
                                                            </Col>

                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="autorenew_checkouts"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Allow auto-renewal of items: </SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <Row>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <RadioGroup
                                                                                    onValueChange={field.onChange}
                                                                                    defaultValue={field.value} value={field.value}
                                                                                    className="flex flex-col space-y-1"
                                                                                >
                                                                                    <FormItem className="flex items-center space-x-3 space-y-0">
                                                                                        <FormControl>
                                                                                            <RadioGroupItem value={1} />
                                                                                        </FormControl>
                                                                                        <FormLabel className="font-normal">
                                                                                            Yes
                                                                                        </FormLabel>
                                                                                    </FormItem>
                                                                                    <FormItem className="flex items-center space-x-3 space-y-0">
                                                                                        <FormControl>
                                                                                            <RadioGroupItem value={0} />
                                                                                        </FormControl>
                                                                                        <FormLabel className="font-normal">
                                                                                            No
                                                                                        </FormLabel>
                                                                                    </FormItem>

                                                                                </RadioGroup>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    </Col>
                                                                </Row>
                                                            </Col>

                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="protected"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Protected: </SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <Row>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <RadioGroup
                                                                                    onValueChange={field.onChange}
                                                                                    defaultValue={field.value} value={field.value}
                                                                                    className="flex flex-col space-y-1"
                                                                                >
                                                                                    <FormItem className="flex items-center space-x-3 space-y-0">
                                                                                        <FormControl>
                                                                                            <RadioGroupItem value={1} />
                                                                                        </FormControl>
                                                                                        <FormLabel className="font-normal">
                                                                                            Yes
                                                                                        </FormLabel>
                                                                                    </FormItem>
                                                                                    <FormItem className="flex items-center space-x-3 space-y-0">
                                                                                        <FormControl>
                                                                                            <RadioGroupItem value={0} />
                                                                                        </FormControl>
                                                                                        <FormLabel className="font-normal">
                                                                                            No
                                                                                        </FormLabel>
                                                                                    </FormItem>

                                                                                </RadioGroup>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    </Col>
                                                                </Row>
                                                            </Col>

                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>
                            {/* Library setup Section */}
                            <Col xxl={8} xl={8} lg={8} md={12} sm={12} xs={24}>
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            Library setup
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="dateenrolled"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Registration date:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <Popover>
                                                                        <PopoverTrigger asChild>
                                                                            <FormControl>
                                                                                <Button
                                                                                    variant={"outline"}

                                                                                >
                                                                                    {field.value ? (
                                                                                        dayjs(field.value).format("MM/DD/YYYY")
                                                                                    ) : (
                                                                                        <span>選擇日期</span>
                                                                                    )}
                                                                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                                                </Button>
                                                                            </FormControl>
                                                                        </PopoverTrigger>
                                                                        <PopoverContent className="w-auto p-0" align="start">
                                                                            <Calendar
                                                                                mode="single"
                                                                                selected={field.value}
                                                                                onSelect={field.onChange}
                                                                                disabled={(date) =>
                                                                                    date > new Date() || date < new Date("1900-01-01")
                                                                                }
                                                                                initialFocus
                                                                                {...form.register("enrolmentperioddate", {
                                                                                    required: false
                                                                                })}
                                                                            />
                                                                        </PopoverContent>
                                                                    </Popover>
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="dateexpiry"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Expiry date (leave blank for auto calc):</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <Popover>
                                                                        <PopoverTrigger asChild>
                                                                            <FormControl>
                                                                                <Button
                                                                                    variant={"outline"}

                                                                                >
                                                                                    {field.value ? (
                                                                                        dayjs(field.value).format("MM/DD/YYYY")
                                                                                    ) : (
                                                                                        <span>選擇日期</span>
                                                                                    )}
                                                                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                                                </Button>
                                                                            </FormControl>
                                                                        </PopoverTrigger>
                                                                        <PopoverContent className="w-auto p-0" align="start">
                                                                            <Calendar
                                                                                mode="single"
                                                                                selected={field.value}
                                                                                onSelect={field.onChange}
                                                                                disabled={(date) =>
                                                                                    date > new Date() || date < new Date("1900-01-01")
                                                                                }
                                                                                initialFocus
                                                                                {...form.register("enrolmentperioddate", {
                                                                                    required: false
                                                                                })}
                                                                            />
                                                                        </PopoverContent>
                                                                    </Popover>
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="opacnote"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>OPAC note:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <Row>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <Em10>
                                                                                    <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                                </Em10>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    </Col>
                                                                    <Col>
                                                                        <FormDescription>
                                                                            This message appears on this patron's user page in the OPAC
                                                                        </FormDescription>
                                                                    </Col>
                                                                </Row>
                                                            </Col>

                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="borrowernotes"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Circulation note:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <Row>
                                                                    <Col>
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <Em10>
                                                                                    <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                                </Em10>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    </Col>
                                                                    <Col>
                                                                        <FormDescription>
                                                                            This message displays when checking out to this patron
                                                                        </FormDescription>
                                                                    </Col>
                                                                </Row>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>
                            {/* OPAC/Staff interface login Section */}
                            <Col xxl={8} xl={8} lg={8} md={12} sm={12} xs={24}>
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            OPAC/Staff interface login
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row gutter={[8, 8]}>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="userid"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Username:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="password"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Password:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormDescription>
                                                                        Minimum password length: 8
                                                                    </FormDescription>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <FormField
                                                    control={form.control}
                                                    name="password2"
                                                    render={({ field }) => (
                                                        <Row gutter={[8, 8]} wrap={false} align="middle">
                                                            <Col>
                                                                <SubTitle>Confirm password:</SubTitle>
                                                            </Col>
                                                            <Col>
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Em10>
                                                                            <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                        </Em10>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>
                            {/* Patron restrictions Section */}
                            <Col xxl={8} xl={12} lg={12} md={12} sm={12} xs={24}>
                                <Card style={{
                                    height: '100%'
                                }}>
                                    <CardHeader>
                                        <TypographyH2 >
                                            Patron restrictions
                                        </TypographyH2>
                                        <FormDescription>
                                            Patron is currently unrestricted.
                                        </FormDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <Row gutter={[8, 8]}>
                                            {
                                                debarredFields.length > 0 && (
                                                    <Row>
                                                        <Col span={24}>
                                                            <Table>
                                                                <TableHeader>
                                                                    <TableRow>
                                                                        <TableHead>Type</TableHead>
                                                                        <TableHead>Comment</TableHead>
                                                                        <TableHead>Expiration</TableHead>
                                                                        <TableHead>Created</TableHead>
                                                                        <TableHead>Remove?</TableHead>
                                                                    </TableRow>
                                                                </TableHeader>
                                                                <TableBody>
                                                                    {debarredFields.map((field, index) => {
                                                                        return (
                                                                            <TableRow key={field.id}>
                                                                                <TableCell>
                                                                                    <FormField
                                                                                        control={form.control}
                                                                                        name={`debarred.${index}.type`}
                                                                                        render={({ field }) => (
                                                                                            <FormItem>
                                                                                                <FormControl>
                                                                                                    <FormLabel
                                                                                                        control={form.control}
                                                                                                        field={field}
                                                                                                        index={index}
                                                                                                        register={{
                                                                                                            ...form.register(`debarred.${index}.type`, {
                                                                                                                required: false,
                                                                                                            })
                                                                                                        }}
                                                                                                    >{ field.value }</FormLabel>
                                                                                                </FormControl>
                                                                                                <FormMessage />
                                                                                            </FormItem>
                                                                                        )}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <FormField
                                                                                        control={form.control}
                                                                                        name={`debarred.${index}.comment`}
                                                                                        render={({ field }) => (
                                                                                            <FormItem>
                                                                                                <FormControl>
                                                                                                    <FormLabel
                                                                                                        control={form.control}
                                                                                                        field={field}
                                                                                                        index={index}
                                                                                                        register={{
                                                                                                            ...form.register(`debarred.${index}.comment`, {
                                                                                                                required: false,
                                                                                                            })
                                                                                                        }}
                                                                                                    >{field.value}</FormLabel>
                                                                                                </FormControl>
                                                                                                <FormMessage />
                                                                                            </FormItem>
                                                                                        )}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <FormField
                                                                                        control={form.control}
                                                                                        name={`debarred.${index}.expiration`}
                                                                                        render={({ field }) => (
                                                                                            <FormItem>
                                                                                                <FormControl>
                                                                                                    <FormLabel
                                                                                                        control={form.control}
                                                                                                        field={field}
                                                                                                        index={index}
                                                                                                        register={{
                                                                                                            ...form.register(`debarred.${index}.expiration`, {
                                                                                                                required: false,
                                                                                                            })
                                                                                                        }}
                                                                                                    >{field.value}</FormLabel>
                                                                                                </FormControl>
                                                                                                <FormMessage />
                                                                                            </FormItem>
                                                                                        )}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <FormField
                                                                                        control={form.control}
                                                                                        name={`debarred.${index}.created`}
                                                                                        render={({ field }) => (
                                                                                            <FormItem>
                                                                                                <FormControl>
                                                                                                    <FormLabel
                                                                                                        control={form.control}
                                                                                                        field={field}
                                                                                                        index={index}
                                                                                                        register={{
                                                                                                            ...form.register(`debarred.${index}.created`, {
                                                                                                                required: false,
                                                                                                            })
                                                                                                        }}
                                                                                                    >{field.value}</FormLabel>
                                                                                                </FormControl>
                                                                                                <FormMessage />
                                                                                            </FormItem>
                                                                                        )}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell className="font-medium">
                                                                                    <FormField
                                                                                        control={form.control}
                                                                                        name={`debarred.${index}.wantDelete`}
                                                                                        render={({ field }) => (
                                                                                            <FormItem>
                                                                                                <FormControl>
                                                                                                    <NormalCheckBox
                                                                                                        control={form.control}
                                                                                                        field={field}
                                                                                                        index={index}
                                                                                                        register={{
                                                                                                            ...form.register(`debarred.${index}.wantDelete`, {
                                                                                                                required: false,
                                                                                                            })
                                                                                                        }}
                                                                                                    >{field.value}</NormalCheckBox>
                                                                                                </FormControl>
                                                                                                <FormMessage />
                                                                                            </FormItem>
                                                                                        )}
                                                                                    />
                                                                                    <FormField
                                                                                        control={form.control}
                                                                                        name={`debarred.${index}.debarred_id`}
                                                                                        render={({ field }) => (
                                                                                            <FormItem>
                                                                                                <FormControl>
                                                                                                    <Input
                                                                                                        hidden={true}
                                                                                                        key={field.id}
                                                                                                        {...field}
                                                                                                        {...form.register(`debarred.${index}.debarred_id`, {
                                                                                                            required: false,
                                                                                                        })}
                                                                                                    />
                                                                                                </FormControl>
                                                                                                <FormMessage />
                                                                                            </FormItem>
                                                                                        )}
                                                                                    />
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>
                                                            </Table>
                                                        </Col>
                                                    </Row>
                                                )
                                            }
                                            <Col>
                                                <Button type={"Button"} onClick={() => {
                                                    const bShow = !showPatronRestrictions;
                                                    setShowPatronRestrictions(bShow)
                                                    if (!bShow) {
                                                        form.setValue("debarredcomment", null)
                                                        form.setValue("debarredexpriation", null)
                                                    }
                                                }}>
                                                    {showPatronRestrictions ? 'Cancel' : 'Add manual restriction'}
                                                </Button>
                                            </Col>
                                            {
                                                showPatronRestrictions && (
                                                    <Row>
                                                        <Col span={24}>
                                                            <Row gutter={[8, 8]}>
                                                                <Col span={24}>
                                                                    <FormField
                                                                        control={form.control}
                                                                        name="debarredcomment"
                                                                        render={({ field }) => (
                                                                            <Row gutter={[8, 8]} wrap={false} align="middle">
                                                                                <Col>
                                                                                    <SubTitle>Comment:</SubTitle>
                                                                                </Col>
                                                                                <Col>
                                                                                    <FormItem>
                                                                                        <FormControl>
                                                                                            <Em10>
                                                                                                <Input key={field.id} placeholder="請輸入..." {...field} />
                                                                                            </Em10>
                                                                                        </FormControl>
                                                                                        <FormMessage />
                                                                                    </FormItem>
                                                                                </Col>
                                                                            </Row>
                                                                        )}
                                                                    />
                                                                </Col>
                                                                <Col span={24}>
                                                                    <FormField
                                                                        control={form.control}
                                                                        name="debarredexpriation"
                                                                        render={({ field }) => (
                                                                            <Row gutter={[8, 8]} wrap={false} align="middle">
                                                                                <Col>
                                                                                    <SubTitle>Expiration:</SubTitle>
                                                                                </Col>
                                                                                <Col>
                                                                                    <FormItem>
                                                                                        <Popover>
                                                                                            <PopoverTrigger asChild>
                                                                                                <FormControl>
                                                                                                    <Button
                                                                                                        variant={"outline"}
                                                                                                    >
                                                                                                        {field.value ? (
                                                                                                            dayjs(field.value).format("MM/DD/YYYY")
                                                                                                        ) : (
                                                                                                            <span>選擇日期</span>
                                                                                                        )}
                                                                                                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                                                                    </Button>
                                                                                                </FormControl>
                                                                                            </PopoverTrigger>
                                                                                            <PopoverContent className="w-auto p-0" align="start">
                                                                                                <Calendar
                                                                                                    mode="single"
                                                                                                    selected={field.value}
                                                                                                    onSelect={field.onChange}
                                                                                                    disabled={(date) =>
                                                                                                        date < new Date("1900-01-01")
                                                                                                    }
                                                                                                    initialFocus
                                                                                                    {...form.register("enrolmentperioddate", {
                                                                                                        required: false
                                                                                                    })}
                                                                                                />
                                                                                            </PopoverContent>
                                                                                        </Popover>
                                                                                    </FormItem>
                                                                                </Col>
                                                                            </Row>
                                                                        )}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                )
                                            }

                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>
                            {/*  Patron messaging preferences Section */}
                            <Col xxl={10} xl={12} lg={12} md={12} sm={12} xs={24}>
                                <Card style={{ height: '100%' }} >
                                    <CardHeader>
                                        <TypographyH2 >
                                            Patron messaging preferences
                                        </TypographyH2>
                                    </CardHeader>
                                    <CardContent>
                                        <Row>
                                            <Col span={24}>
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead> </TableHead>
                                                            <TableHead>Days in advance</TableHead>
                                                            <TableHead>Email</TableHead>
                                                            <TableHead>Digests only</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {messageFields.map((field, index) => {
                                                            return (
                                                                <TableRow key={field.id}>
                                                                    <TableCell className="font-medium">
                                                                        <MessageName
                                                                            control={form.control}
                                                                            fieldId={index}
                                                                        />
                                                                        <FormField
                                                                            control={form.control}
                                                                            name={`message.${index}.message_attribute_id`}
                                                                            render={({ field }) => (
                                                                                <FormItem>
                                                                                    <FormControl>
                                                                                        <Input
                                                                                            hidden={true}
                                                                                            key={field.id}
                                                                                            {...field}
                                                                                            {...form.register(`message.${index}.message_attribute_id`, {
                                                                                                required: false,
                                                                                            })}
                                                                                        />
                                                                                    </FormControl>
                                                                                    <FormMessage />
                                                                                </FormItem>
                                                                            )}
                                                                        />
                                                                        <FormField
                                                                            control={form.control}
                                                                            name={`message.${index}.borrower_message_preference_id`}
                                                                            render={({ field }) => (
                                                                                <FormItem>
                                                                                    <FormControl>
                                                                                        <Input
                                                                                            hidden={true}
                                                                                            key={field.id}  {...field}
                                                                                            {...form.register(`message.${index}.borrower_message_preference_id`, {
                                                                                                required: false,
                                                                                            })}
                                                                                        />
                                                                                    </FormControl>
                                                                                    <FormMessage />
                                                                                </FormItem>
                                                                            )}
                                                                        />
                                                                        <FormField
                                                                            control={form.control}
                                                                            name={`message.${index}.message_name`}
                                                                            render={({ field }) => (
                                                                                <FormItem>
                                                                                    <FormControl>
                                                                                        <Input
                                                                                            hidden={true}
                                                                                            key={field.id}
                                                                                            {...field}
                                                                                            {...form.register(`message.${index}.message_name`, {
                                                                                                required: false,
                                                                                            })}
                                                                                        />
                                                                                    </FormControl>
                                                                                    <FormMessage />
                                                                                </FormItem>
                                                                            )}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <FormField
                                                                            control={form.control}
                                                                            name={`message.${index}.days_in_advance`}
                                                                            render={({ field }) => (
                                                                                <FormItem>
                                                                                    <FormControl>
                                                                                        <DaysInAdvance
                                                                                            control={form.control}
                                                                                            field={field}
                                                                                            index={index}
                                                                                            register={{
                                                                                                ...form.register(`message.${index}.days_in_advance`, {
                                                                                                    required: false,
                                                                                                    setValueAs: (v) => v === "" ? undefined : parseInt(v, 10),
                                                                                                })
                                                                                            }}
                                                                                        />
                                                                                    </FormControl>
                                                                                    <FormMessage />
                                                                                </FormItem>
                                                                            )}
                                                                        /></TableCell>
                                                                    <TableCell>
                                                                        <FormField
                                                                            control={form.control}
                                                                            name={`message.${index}.email`}
                                                                            render={({ field }) => (
                                                                                <FormItem>
                                                                                    <FormControl>
                                                                                        <NormalCheckBox
                                                                                            control={form.control}
                                                                                            field={field}
                                                                                            index={index}
                                                                                            register={{
                                                                                                ...form.register(`message.${index}.email`, {
                                                                                                    required: false,
                                                                                                    // setValueAs: (v) => v === "" ? undefined : parseInt(v, 10),
                                                                                                })
                                                                                            }}
                                                                                        />
                                                                                    </FormControl>
                                                                                    <FormMessage />
                                                                                </FormItem>
                                                                            )}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell  >
                                                                        <FormField
                                                                            control={form.control}
                                                                            name={`message.${index}.canset_digest`}
                                                                            render={({ field }) => (
                                                                                <FormItem>
                                                                                    <FormControl>
                                                                                        {<Input
                                                                                            hidden={true}
                                                                                            key={field.id}
                                                                                            {...field}

                                                                                        />}

                                                                                    </FormControl>
                                                                                    <FormMessage />
                                                                                </FormItem>
                                                                            )}
                                                                        />
                                                                        <FormField
                                                                            control={form.control}
                                                                            name={`message.${index}.wants_digest`}
                                                                            render={({ field }) => (
                                                                                <FormItem>
                                                                                    <FormControl>
                                                                                        {<DigestsOnlyCheck
                                                                                            control={form.control}
                                                                                            field={field}
                                                                                            index={index}
                                                                                            register={{
                                                                                                ...form.register(`message.${index}.wants_digest`, {
                                                                                                    required: false,
                                                                                                    setValueAs: (v) => v === "" ? undefined : parseInt(v, 10),
                                                                                                })
                                                                                            }}
                                                                                        />}

                                                                                    </FormControl>
                                                                                    <FormMessage />
                                                                                </FormItem>
                                                                            )}
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </Col>
                                        </Row>
                                    </CardContent>
                                </Card>
                            </Col>
                        </Row>
                    </form>
                </Form>
            </Col>
        </Row>

    )
})


const SubTitle = styled.div`
text-align: left;
// width: 90%;
font-size: 1.2em;
font-weight: 600;
`

const Em10 = styled.div`
width: 10em;
display: flex;
justify-content: flex-start;
align-items: center;
gap: 0.3em;
`


export default PatronForm